import {
	IState,
	GetPrincipalResponse,
	ListPrincipalsResponse,
	GetPolicyResponse,
	ListPoliciesResponse,
	ResponseWitOutData,
	CreateObjectRequest,
	GetObjectResponse,
	ListObjectsResponse,
	SubjectStateResponse,
	ListDeliveriesResponse,
	GetDeliveryResponse,
	AuthenticateResponse,
	GetStateRequest,
	ApiKeyRequest,
	CreateApiKeyResponse,
	ListApiKeysResponse,
	SetStateResponse,
	BSHttpOptions,
	Definition,
	CreatePrincipalRequest,
	SetValueRefResponse,
	GetStateRefRequest,
	GetValueRefResponse,
	ValueListRefResponse,
	RegisterResponse,
	CreateKeyPairResponse,
	PublicKey,
	ListPublicKeysResponse,
	CreateDeliveryRequest,
	GetApiKeyInfoResponse,
	VersionListResponse,
	ResponseAction,
	GetListRequest,
	GetListSegmentRequest,
	ListSegmentsResponse,
	GetListByPrincipalRequest,
	SingOutRequest,
	GetValueListRequest,
	SetValueRefRequest,
	FileType,
} from './types';
import { setErrorHandler } from './handleError';
import stateService from './stateService/stateService';
import authService from './authService/authService';
import deliveryService from './deliveryService/deliveryService';
import securityPolicyService from './securityPolicyService/securityPolicyService';
import securityPrincipalService from './securityPrincipalService/securityPrincipalService';
import objectService from './objectService/objectService';
import apiKeyService from './apiKeyService/apiKeyService';

class BigStateHttpClient {
	private baseUrl: string = '';
	private _apiKey: string = '';
	private _token: string = '';

	constructor(data: BSHttpOptions) {
		if (!data?.baseUrl?.trim()) {
			throw new Error('baseUrl is required');
		}
		this.baseUrl = `${data.baseUrl}/v1`;
		const { token, apiKey, errorHandler } = data;
		token && (this._token = token);
		apiKey && (this._apiKey = apiKey);
		setErrorHandler(errorHandler);
	}

	set apiKey(value: string) {
		this._apiKey = value;
	}

	set token(value: string) {
		this._token = value;
	}

	private get authKey() {
		return { apiKey: this._apiKey, token: this._token };
	}

	async signUp(): Promise<RegisterResponse> {
		return await authService.signUp(this.baseUrl);
	}

	async creatKeyPrincipal(principal: string): Promise<CreateKeyPairResponse> {
		return await authService.creatKeyPrincipal(
			this.baseUrl,
			principal,
			this.authKey,
		);
	}

	async deleteKeyPrincipal(
		principal: string,
		data: PublicKey,
	): Promise<CreateKeyPairResponse> {
		return await authService.deleteKeyPrincipal(
			this.baseUrl,
			principal,
			data,
			this.authKey,
		);
	}

	async getListKeyPrincipal(
		data: GetListByPrincipalRequest,
	): Promise<ListPublicKeysResponse> {
		return await authService.getListKeyPrincipal(
			this.baseUrl,
			data,
			this.authKey,
		);
	}

	async signInPrincipal(principal: string): Promise<AuthenticateResponse> {
		return await authService.signInPrincipal(
			this.baseUrl,
			principal,
			this.authKey,
		);
	}

	async signOutPrincipal(data: SingOutRequest): Promise<ResponseWitOutData> {
		return await authService.signOutPrincipal(this.baseUrl, data, this.authKey);
	}

	async getAuthInfo(): Promise<GetApiKeyInfoResponse> {
		return await authService.authInfo(this.baseUrl, this.authKey);
	}

	async createApiKey(
		principal: string,
		data: ApiKeyRequest,
	): Promise<CreateApiKeyResponse> {
		return await apiKeyService.create(
			this.baseUrl,
			principal,
			data,
			this.authKey,
		);
	}

	async deleteApiKey(
		principal: string,
		data: ApiKeyRequest,
	): Promise<ResponseWitOutData> {
		return await apiKeyService.delete(
			this.baseUrl,
			principal,
			data,
			this.authKey,
		);
	}

	async getListApiKey(
		data: GetListByPrincipalRequest,
	): Promise<ListApiKeysResponse> {
		return await apiKeyService.getList(this.baseUrl, data, this.authKey);
	}

	async objectCreate(
		object: string,
		data?: CreateObjectRequest,
	): Promise<ResponseAction> {
		return await objectService.create(this.baseUrl, object, this.authKey, data);
	}

	async objectGet(object: string): Promise<GetObjectResponse> {
		return await objectService.get(this.baseUrl, object, this.authKey);
	}

	async objectDelete(object: string): Promise<ResponseAction> {
		return await objectService.delete(this.baseUrl, object, this.authKey);
	}

	async objectGetList(data?: GetListRequest): Promise<ListObjectsResponse> {
		return await objectService.getList(this.baseUrl, this.authKey, data);
	}

	async objectGetSegmentList(
		data?: GetListSegmentRequest,
	): Promise<ListSegmentsResponse> {
		return await objectService.getSegmentList(this.baseUrl, this.authKey, data);
	}

	async stateSet<T = any>(
		object: string,
		data: IState<T>,
	): Promise<SetStateResponse> {
		return await stateService.set<T>(this.baseUrl, object, data, this.authKey);
	}

	async stateSetValueRef(
		data: SetValueRefRequest,
		file: FileType,
	): Promise<SetValueRefResponse> {
		return await stateService.setValueRef(
			this.baseUrl,
			data,
			file,
			this.authKey,
		);
	}

	async stateGet<T = any>(
		data: GetStateRequest,
	): Promise<SubjectStateResponse<T>> {
		return await stateService.get<T>(this.baseUrl, data, this.authKey);
	}

	async stateGetValueRef(
		data: GetStateRefRequest,
	): Promise<GetValueRefResponse> {
		return await stateService.getValueRef(this.baseUrl, data, this.authKey);
	}

	async stateDeleteValueRef(
		data: GetStateRefRequest,
	): Promise<ResponseWitOutData> {
		return await stateService.deleteValueRef(this.baseUrl, data, this.authKey);
	}

	async getVersionList(object: string): Promise<VersionListResponse> {
		return await stateService.getVersionList(
			this.baseUrl,
			object,
			this.authKey,
		);
	}

	async getRefList(data: GetValueListRequest): Promise<ValueListRefResponse> {
		return await stateService.getRefList(this.baseUrl, data, this.authKey);
	}

	async principalCreate(
		principal: string,
		data: CreatePrincipalRequest,
	): Promise<ResponseWitOutData> {
		return await securityPrincipalService.create(
			this.baseUrl,
			principal,
			data,
			this.authKey,
		);
	}

	async principalGet(principal: string): Promise<GetPrincipalResponse> {
		return await securityPrincipalService.get(
			this.baseUrl,
			principal,
			this.authKey,
		);
	}

	async principalGetList(
		data?: GetListRequest,
	): Promise<ListPrincipalsResponse> {
		return await securityPrincipalService.getList(
			this.baseUrl,
			data,
			this.authKey,
		);
	}

	async principalDelete(principal: string): Promise<ResponseWitOutData> {
		return await securityPrincipalService.delete(
			this.baseUrl,
			principal,
			this.authKey,
		);
	}

	async policyCreate(
		policy: string,
		data: Definition,
	): Promise<ResponseWitOutData> {
		return await securityPolicyService.create(
			this.baseUrl,
			policy,
			data,
			this.authKey,
		);
	}

	async policyDelete(policy: string): Promise<ResponseWitOutData> {
		return await securityPolicyService.delete(
			this.baseUrl,
			policy,
			this.authKey,
		);
	}

	async policyGet(policy: string): Promise<GetPolicyResponse> {
		return await securityPolicyService.get(this.baseUrl, policy, this.authKey);
	}

	async policyGetList(data?: GetListRequest): Promise<ListPoliciesResponse> {
		return await securityPolicyService.getList(
			this.baseUrl,
			data,
			this.authKey,
		);
	}

	async deliveryCreate(
		delivery: string,
		data: CreateDeliveryRequest,
	): Promise<ResponseWitOutData> {
		return await deliveryService.create(
			this.baseUrl,
			delivery,
			data,
			this.authKey,
		);
	}

	async deliveryDelete(delivery: string): Promise<ResponseWitOutData> {
		return await deliveryService.delete(this.baseUrl, delivery, this.authKey);
	}

	async deliveryGet(delivery: string): Promise<GetDeliveryResponse> {
		return await deliveryService.get(this.baseUrl, delivery, this.authKey);
	}

	async deliveryGetList(
		data?: GetListRequest,
	): Promise<ListDeliveriesResponse> {
		return await deliveryService.getList(this.baseUrl, data, this.authKey);
	}
}

export default BigStateHttpClient;
